import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../constants/api";
import CollapsibleBlock from "../ui/CollapsibleBlock";
import { useTheme } from "../../context/ThemeContext.jsx";
import ZuweisungBadge from "../ui/ZuweisungBadge.jsx";
import AppButton from "../ui/AppButton.jsx";
const toTime = v => {
  if (!v) return 0;
  const s = /^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v}T12:00:00` : v;
  const t = new Date(s).getTime();
  return Number.isFinite(t) ? t : 0;
};
const fmtDate = v => v ? new Date(toTime(v)).toLocaleDateString("de-CH") : "—";
const D = 24 * 60 * 60 * 1000;
const relDue = v => {
  const t = toTime(v);
  if (!t) return "—";
  const diff = Math.round((t - Date.now()) / D);
  if (diff === 0) return "Heute";
  if (diff === 1) return "Morgen";
  if (diff > 1) return `in ${diff} Tagen`;
  if (diff === -1) return "Gestern fällig";
  return `${Math.abs(diff)} Tage überfällig`;
};
export default function ObjektBlockPendenzen({
  objektId,
  onEmpty
}) {
    const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const abortRef = useRef(null);
  const mountedRef = useRef(true);
  const navigate = useNavigate();
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort?.();
    };
  }, []);
  const load = useCallback(async () => {
    if (!objektId) return;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      if (mountedRef.current) setLoading(true);
      const {
        data
      } = await api.get(`/pendenzen/objekt/${objektId}`, {
        params: {
          status: "offen"
        },
        signal: controller.signal
      });
      const list = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
      const enriched = list.map(it => ({
        ...it,
        _ts: toTime(it.aktualisiert_am || it.updated_at || it.created_at || it.erzeugt_am || it.faellig_am)
      })).sort((a, b) => (b._ts || 0) - (a._ts || 0));
      if (!mountedRef.current || abortRef.current !== controller) return;
      setItems(enriched);
      if (enriched.length === 0) onEmpty?.();
    } catch (e) {
      if (e?.name !== "CanceledError" && e?.code !== "ERR_CANCELED") {
        if (!mountedRef.current || abortRef.current !== controller) return;
        setItems([]);
        onEmpty?.();
      }
    } finally {
      if (mountedRef.current && abortRef.current === controller) setLoading(false);
    }
  }, [objektId, onEmpty]);
  useEffect(() => {
    load();
  }, [objektId]);
  const overdueCount = useMemo(() => items.filter(it => toTime(it.faellig_am) && toTime(it.faellig_am) < Date.now()).length, [items]);
  const headerRight = useMemo(() => {
    const n = items.length;
    return `${n} offen${overdueCount ? ` · ${overdueCount} überfällig` : ""}`;
  }, [items.length, overdueCount]);
  if (loading) {
    const loadCls = "glass rounded-2xl p-4 border border-white/10";
    const barCls = "bg-white/10";
    return <section className={loadCls}>
        <div className={`h-5 w-48 rounded mb-3 animate-pulse ${barCls}`} />
        <div className={`h-14 rounded animate-pulse ${barCls}`} />
      </section>;
  }
  if (!items.length) return null;
  const openItem = it => {
    navigate(`/meine-aufgaben?tab=pendenzen&open=${it.id}`);
  };
  const statusPill = s => s === "offen" ? "border-amber-400/30 bg-amber-400/10 text-amber-200" : s === "erledigt" ? "border-green-400/30 bg-green-400/10 text-green-200" : "border-white/15 bg-white/10 text-blue-200";
  return <CollapsibleBlock compact title="📝 Pendenzen" headerInfo={headerRight} panelId="objekt-pendenzen-panel" defaultOpen={false} contentClassName="px-3 sm:px-4 pb-3 sm:pb-4">
      <div className="space-y-2">
        {items.map(it => {
        const dueStr = fmtDate(it.faellig_am);
        const rel = relDue(it.faellig_am);
        const overdue = toTime(it.faellig_am) && toTime(it.faellig_am) < Date.now();
        const btnCls = "w-full text-left rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition p-3";
        return <AppButton key={it.id} type="button" variant="ghost" onClick={() => openItem(it)} title="Pendenz öffnen" className={`${btnCls} !justify-start min-h-0`}>
              <div className="grid grid-cols-[1fr_auto] items-start gap-3">
                <div className="min-w-0">
                  <div className={`font-semibold truncate ${"text-blue-100"}`}>{it.titel || `Pendenz #${it.id}`}</div>
                </div>
                <div className={`text-xs whitespace-nowrap ${overdue ? "text-red-500" : "text-blue-200/70"}`}>
                  Fällig: {dueStr} · {rel}
                </div>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
                {it.status && <span className={`px-2 py-0.5 rounded-md border ${statusPill(it.status)}`}>{it.status}</span>}
                <ZuweisungBadge name={it.assignee_name || "—"} variant="full" size="xs" truncateAt={18} title={it.assignee_name || "Zuständig"} />
              </div>
              {it.beschreibung && <div className={`mt-1 text-xs line-clamp-2 ${"text-blue-100/90"}`}>{it.beschreibung}</div>}
            </AppButton>;
      })}
      </div>
    </CollapsibleBlock>;
}